import { withNamespaces } from 'react-i18next';
import { Button, Card, CardBody, Col, Row } from 'reactstrap';

import BidVerificationFlow from '../../flow/components/BidVerificationFlow';
import useBidVerificationFlow from '../../flow/hooks/useBidVerificationFlow';
import { SIMULATION_STATUS } from '../../flow/hooks/useSimulationClock';
import getLiveVerificationStats from './getLiveVerificationStats';

const formatClock = (milliseconds) => {
    const totalSeconds = Math.max(0, Math.floor(milliseconds / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');

    return `${minutes}:${seconds}`;
};

const LiveVerification = ({
    bid,
    t,
}) => {
    const {
        duration,
        elapsed,
        getActiveSignals,
        getEdgeTrails,
        liveBranchId,
        nodes,
        pause,
        relationships,
        reset,
        start,
        status,
    } = useBidVerificationFlow(bid);
    const stats = getLiveVerificationStats(nodes, relationships, bid);
    const isLive = status === SIMULATION_STATUS.running;
    const summaryCards = [
        {
            icon: 'ri-checkbox-circle-fill',
            iconClass: 'text-success',
            value: `${stats.agentsCompleted} / ${stats.agentsTotal}`,
            label: 'AI agents completed',
        },
        {
            icon: 'ri-checkbox-blank-line',
            iconClass: 'text-secondary',
            value: stats.checksPerformed,
            label: 'Checks performed',
        },
        {
            icon: 'ri-user-3-line',
            iconClass: 'text-secondary',
            value: stats.dataPoints,
            label: 'Data points extracted',
        },
        {
            icon: 'ri-hashtag',
            iconClass: 'text-secondary',
            value: stats.crossChecks,
            label: 'Cross-checks executed',
        },
        {
            icon: 'ri-error-warning-line',
            iconClass: stats.findings ? 'text-danger' : 'text-secondary',
            value: stats.findings,
            label: 'Findings require action',
            valueClass: stats.findings ? 'text-danger' : '',
        },
    ];
    const progressItems = [
        { label: 'Categories', value: stats.categories },
        { label: 'Total checks', value: stats.checksTotal },
        { label: 'Completed', value: stats.completed },
        { label: 'In progress', value: stats.inProgress },
        { label: 'Pending', value: stats.pending },
    ];

    return (
        <Card className="live-verification-card">
            <CardBody>
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4">
                    <div>
                        <h5 className="mb-1">{t('Live Verification')}</h5>
                        <p className="text-muted mb-0">
                            {t('Powered by {{count}} specialized AI agents', {
                                count: stats.agentsTotal,
                            })}
                        </p>
                    </div>

                    <Button className="p-0" color="link" type="button">
                        <i className="ri-question-line align-middle me-1" />
                        {t('How it works?')}
                    </Button>
                </div>

                <Row className="g-3 mb-4">
                    {summaryCards.map((card) => (
                        <Col key={card.label} md>
                            <div className="live-verification-box live-verification-stat">
                                <i className={`${card.icon} ${card.iconClass}`} />
                                <div className={`live-verification-stat-value ${card.valueClass || ''}`}>
                                    {card.value}
                                </div>
                                <span className="text-muted font-size-12">{t(card.label)}</span>
                            </div>
                        </Col>
                    ))}
                </Row>

                <div className="live-verification-progress mb-3">
                    <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-1">
                                <h5 className="mb-0">{t('Live Verification')}</h5>
                                <span className={`badge ${isLive ? 'badge-soft-success' : 'badge-soft-secondary'}`}>
                                    {t(isLive ? 'Real-time' : status === SIMULATION_STATUS.finished ? 'Completed' : 'Paused')}
                                </span>
                            </div>
                            <p className="text-muted mb-0">
                                {t('Multiple checks running in parallel using trusted global data sources.')}
                            </p>
                        </div>

                        <div className="d-flex flex-wrap align-items-center gap-4">
                            <span className="live-verification-timer">
                                <i className="ri-time-line align-middle me-1" />
                                {`${formatClock(elapsed)} / ${formatClock(duration)}`}
                            </span>

                            {progressItems.map((item) => (
                                <span className="text-center" key={item.label}>
                                    <span className="d-block fw-medium">{item.value}</span>
                                    <span className="text-muted font-size-12">{t(item.label)}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                <BidVerificationFlow
                    bid={bid}
                    getActiveSignals={getActiveSignals}
                    getEdgeTrails={getEdgeTrails}
                    liveBranchId={liveBranchId}
                    nodes={nodes}
                    onPause={pause}
                    onReset={reset}
                    onStart={start}
                    relationships={relationships}
                    showHeader={false}
                    status={status}
                />
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(LiveVerification);
